import { IconButton, Stack, Typography } from '@mui/material';
import { MAX_PAGE_SIZE, RootEntityType } from '@schooly/api';
import { UpdateAllThreadsComponent } from '@schooly/components/threads';
import { CrossIcon, EditIcon, Loading, ModalHeader, ModalLarge } from '@schooly/style';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router';

import { AccessDenied } from '@schooly/style';
import { ModalContent, ModalMain } from '../../../components/uikit-components/Modal/Modal.styled';
import { ModalHeaderV2 } from '../../../components/uikit-components/Modal/ModalHeaderV2';
import { useMessage } from '../../../context/messages/useMessage';
import { isOnlyGroupAndDateFilled } from '../../../helpers/messages';
import useAppLocation from '../../../hooks/useAppLocation';
import useQueryStringParams from '../../../hooks/useQueryStringParams';
import { useSchool } from '../../../hooks/useSchool';
import { MessagesDeleteGroupModal } from '../MessagesCreateModal/confirmations/MessagesDeleteGroupModal';
import { MessageModalPreviewActions } from './MessageModalPreviewActions';
import { MessageModalPreviewMessageInfo } from './MessageModalPreviewMessageInfo';
import { MessageModalPreviewThreads } from './MessageModalPreviewThreads/MessageModalPreviewThreads';
import { MessageModalPreviewMode, MessagePreviewModalTabs } from './MessagePreviewModalTabs';
import { MessagesModalPreviewConsentForm } from './MessagesModalPreviewConsentForm';
import { MessagesModalPreviewRecipients } from './MessagesModalPreviewReadStatistics/MessagesModalPreviewReadStatistics';

export const MessagesPreviewModal: React.FC = () => {
  const {
    messageId,
    canView,
    title,
    fetching,
    canEdit,
    actions,
    messageData,
    form,
    messageFormFields,
    criteriaDate,
    isRemoveGroupDialogOpen,
    notActualGroups,
    hasThreads,
    hasAccessToThreads,
    submitAction,
  } = useMessage();

  const { schoolId } = useSchool();
  const navigate = useNavigate();
  const location = useAppLocation();
  const { tab } = useQueryStringParams();

  const allGroupsInvalid = useMemo(
    () =>
      notActualGroups.length ===
      new Set([
        ...(messageFormFields?.parents_of_students_criteria.group || []),
        ...(messageFormFields?.staff_criteria.group || []),
      ]).size,
    [notActualGroups, messageFormFields],
  );

  const onlyGroupsSelected = useMemo(() => {
    const parentsValid =
      messageFormFields?.parents_of_students_criteria &&
      isOnlyGroupAndDateFilled(messageFormFields?.parents_of_students_criteria);

    const staffValid =
      messageFormFields?.staff_criteria &&
      isOnlyGroupAndDateFilled(messageFormFields?.staff_criteria);

    return parentsValid && staffValid;
  }, [messageFormFields]);

  const handleEditButtonClick = useCallback(() => {
    form.reset(messageFormFields);
    navigate(`/messages/${messageId}/edit`);
  }, [navigate, messageId, form, messageFormFields]);

  const initialTab =
    hasThreads && hasAccessToThreads
      ? MessageModalPreviewMode.THREADS
      : MessageModalPreviewMode.MESSAGE_INFO;

  useEffect(() => {
    if (!tab && !fetching) {
      navigate(
        {
          search: `?tab=${initialTab}`,
        },
        { replace: true, state: location.state },
      );
    }
  });

  const groupsChecked = useRef(false);

  useEffect(() => {
    if (!groupsChecked.current && !fetching) {
      groupsChecked.current = true;

      actions.checkInvalidGroups(
        {
          schoolId: schoolId || '',
          singleDate: criteriaDate,
          pageSize: MAX_PAGE_SIZE,
          min: true,
        },
        true,
      );
    }
  }, [actions, schoolId, criteriaDate, fetching]);

  const showEditButton = !fetching && canEdit;

  const canEditThreads = hasThreads && hasAccessToThreads;

  const renderContent = () => {
    if (fetching) {
      return <Loading />;
    }

    if (!canView || !messageData) {
      return (
        <>
          <ModalHeader withBorderBottom={false} active>
            <IconButton onClick={actions.handleClose}>
              <CrossIcon />
            </IconButton>
          </ModalHeader>
          <AccessDenied />
        </>
      );
    }

    return (
      <>
        <ModalHeaderV2
          withGoBack
          active
          title={<Typography variant="h1">{title}</Typography>}
          multiline
          subHeader={<MessagePreviewModalTabs />}
        >
          {showEditButton && (
            <IconButton onClick={handleEditButtonClick}>
              <EditIcon />
            </IconButton>
          )}
          <MessageModalPreviewActions />

          {canEditThreads && schoolId && messageId && (
            <Stack flex={1} alignItems="flex-end" justifyContent="center">
              <UpdateAllThreadsComponent
                schoolId={schoolId}
                messageId={messageId}
                rootEntityType={RootEntityType.MESSAGE}
              />
            </Stack>
          )}

          <IconButton onClick={actions.handleClose}>
            <CrossIcon />
          </IconButton>
        </ModalHeaderV2>

        <ModalMain>
          <ModalContent
            sx={(theme) => ({
              pt: theme.spacing(2.5),
              p: tab === MessageModalPreviewMode.THREADS ? 0 : undefined,
            })}
          >
            {tab === MessageModalPreviewMode.THREADS &&
              hasThreads &&
              hasAccessToThreads &&
              !fetching && <MessageModalPreviewThreads />}
            {tab === MessageModalPreviewMode.MESSAGE_INFO && <MessageModalPreviewMessageInfo />}
            {tab === MessageModalPreviewMode.READING_STATISTICS && (
              <MessagesModalPreviewRecipients />
            )}
            {tab === MessageModalPreviewMode.CONSENT_FORM && <MessagesModalPreviewConsentForm />}

            <MessagesDeleteGroupModal
              isOpen={isRemoveGroupDialogOpen}
              invalidGroups={notActualGroups}
              allInvalid={allGroupsInvalid && onlyGroupsSelected}
              onClose={actions.handleCloseRemoveGroupDialog}
              onConfirm={() => actions.handleConfirmRemoveGroupDialog(submitAction)}
              onEdit={actions.handleEditRemoveGroupDialog}
            />
          </ModalContent>
        </ModalMain>
      </>
    );
  };

  return (
    <ModalLarge open onClose={actions.handleClose}>
      {renderContent()}
    </ModalLarge>
  );
};
